import React from 'react';

import useResizeObserver from '@react-hook/resize-observer';

import { ErrorBoundary } from '@nxte-nl/error-boundary';

import { ClientHeightEntry } from '../../components/CollapsePanel/CollapsePanel';
import { Header } from '../../components/Header/Header';
import { HeaderV2 } from '../../components/HeaderV2/HeaderV2';
import {
  HiddenGoogleRecaptcha,
} from '../../components/HiddenGoogleRecaptcha/HiddenGoogleRecaptcha';
import { RedeemHeader } from '../../components/RedeemHeader/RedeemHeader';
import { StoreSwitcherBlock } from '../../components/StoreSwitcherBlock/StoreSwitcherBlock';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { layoutHooks } from '../../hooks/layoutHooks/layoutHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { orderHooks } from '../../hooks/orderHooks/orderHooks';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { AppDataStoreData, BasketMessages, GlobalMessages } from '../../types/Messages';
import {
  FeatureToggleWrapContainer,
} from '../FeatureToggleWrapContainer/FeatureToggleWrapContainer';

export type Props = {
  disableActions?: boolean;
  disableSearch?: boolean;
  informativeMessage?: string | null;
  isHeaderV2?: boolean;
  isRedeem?: boolean;
  loading?: boolean;
  showInformativeMessage?: boolean;
};

export const hooks = {
  useSetHeightCallback(
    setHeight: React.Dispatch<React.SetStateAction<number>>,
  ) {
    return React.useCallback((e: ClientHeightEntry) => {
      setHeight(e.target.clientHeight);
    }, [setHeight]);
  },
  useGetStates() {
    const [height, setHeight] = React.useState<number>(0);
    const language = messageHooks.useSubjectState(GlobalMessages.Language);
    const cart = messageHooks.useSubjectState(BasketMessages.Cart);
    const cartQuantity = messageHooks.useSubjectState(GlobalMessages.CartQuantity);
    return {
      height,
      setHeight,
      language,
      cart,
      cartQuantity,
    };
  },
  useUpdateSwitcherBlockHeight(
    ref: React.RefObject<HTMLDivElement>,
    onClientHeightChange: (e: ClientHeightEntry) => void,
  ) {
    useResizeObserver(ref, onClientHeightChange);
  },
  useDesktopBreakPoint(isNavigationIconTextOn: boolean) {
    const { isDesktopLarge } = layoutHooks.useDesktopLarge();
    const { isDesktop } = layoutHooks.useDesktop();

    return isNavigationIconTextOn ? isDesktopLarge : isDesktop;
  },
};

export const HeaderContainer = React.memo(({
  disableActions,
  disableSearch,
  isRedeem = false,
  isHeaderV2 = false,
  loading,
  showInformativeMessage,
  informativeMessage,
}: Props) => {
  const { orderModalAuthMode } = messageHooks.useSubjectState(GlobalMessages.FeatureToggles);
  const { redeemDelayWarning, isNavigationIconTextOn } = featureStateHooks.useFeatureToggles();

  const isDesktop = hooks.useDesktopBreakPoint(isNavigationIconTextOn);

  const layoutExtension = layoutHooks.useLayoutExtension();

  const {
    isBusinessPageV3,
    isGoogleRecaptchaBadgeHidden,
    isLogosFromCBEOn,
  } = featureStateHooks.useFeatureToggles();

  const {
    executeRequest: getOrderQuantity,
  } = orderHooks.useHandleGetCartQuantity();

  orderHooks.useUpdateOrderQuantity(getOrderQuantity);

  const {
    height: storeSwitcherHeight,
    setHeight,
    language,
    cart,
    cartQuantity,
  } = hooks.useGetStates();

  const onClientHeightChange = hooks.useSetHeightCallback(setHeight);

  const refSwitcherBlock = React.useRef<HTMLDivElement>(null);
  hooks.useUpdateSwitcherBlockHeight(refSwitcherBlock, onClientHeightChange);

  const basketQuantity = orderHooks.useOrderItemsQuantity(cart ?? cartQuantity, isRedeem);

  const storeData = messageHooks.useSubjectState(AppDataStoreData.StoreData);

  return (
    <ErrorBoundary>
      <>
        <HiddenGoogleRecaptcha isBadgeHidden={isGoogleRecaptchaBadgeHidden}/>
        <FeatureToggleWrapContainer>
          {({ languageSwitcher }: FeatureToggleValues) => (
            <>
              {languageSwitcher && (
                <StoreSwitcherBlock ref={refSwitcherBlock} setHeight={setHeight} />
              )}
            </>
          )}
        </FeatureToggleWrapContainer>
        {
          isRedeem
            ? <RedeemHeader
              layoutExtension={layoutExtension}
              showDelayWarning={redeemDelayWarning}
              mainLogoFromCBE={storeData?.mainLogo}
              isLogosFromCBEOn={isLogosFromCBEOn}
              showInformativeMessage={showInformativeMessage}
              informativeMessage={informativeMessage}
            />

            : (
              isHeaderV2 && isBusinessPageV3
                ? <HeaderV2
                  businessMenu={storeData?.menus?.businessMenu}
                  loading={loading}
                  isNavigationIconTextOn={isNavigationIconTextOn}
                  disableActions={!!disableActions}
                  disableSearch={!!disableSearch}
                  isDesktop={isDesktop}
                  orderModalAuthMode={orderModalAuthMode}
                  storeSwitcherHeight={storeSwitcherHeight}
                  language={language}
                  basketQuantity={basketQuantity}
                  isBusinessPageV3={isBusinessPageV3}
                />
                : <Header
                  loading={loading}
                  isNavigationIconTextOn={isNavigationIconTextOn}
                  disableActions={!!disableActions}
                  disableSearch={!!disableSearch}
                  isDesktop={isDesktop}
                  orderModalAuthMode={orderModalAuthMode}
                  storeSwitcherHeight={storeSwitcherHeight}
                  language={language}
                  basketQuantity={basketQuantity}
                  mainLogoFromCBE={storeData?.mainLogo}
                  isLogosFromCBEOn={isLogosFromCBEOn}
                />
            )
        }
      </>
    </ErrorBoundary>
  );
});

HeaderContainer.displayName = 'HeaderContainer';
